import { useResizableColumns, ResizableTable, ResizableTh } from "@/components/ui/resizable-table";   // Step 11d · add-on: adjustable columns
import { asArray } from "@/lib/asArray";
import SignOffBar from "@/components/SignOffBar";   // Batch 11 · A2
import React, { useEffect, useState, useMemo, useCallback } from "react";
import { fmtDate as fmtDateDt } from "@/lib/dt";
import { useSearchParams } from "react-router-dom";
import api, { API_URL, toErrMsg } from "@/lib/api";
import { useEngagement } from "@/contexts/EngagementContext";
import { useI18n } from "@/contexts/I18nContext";
import { refLabel } from "@/lib/wsNames";   // Batch 16 · Q10
import { fmtPlural } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Download, Check, AlertTriangle, Filter, ChevronLeft, ArrowUpDown, FileText,
  Plus, Trash2, Pencil, Wand2,
} from "lucide-react";
import { toast } from "sonner";
import { LockedBanner } from "@/components/SignOffCard";
import VouchingTable from "@/components/VouchingTable";
import { assertionLabel } from "@/components/VouchingAssertionLabel";   // PR 56 review · R56-3
import { ReviewControls, ReviewCountsProvider } from "@/components/reviewpoints/ReviewPointsPanel";
import Ref from "@/components/Ref";
import { askConfirm } from "@/lib/appDialog";   // Batch 15 · N4 (F-118)
import TableSaveBar from "@/components/TableSaveBar";   // Batch 28 · Follow-up 1 · F2 (b)
import { useTableDraft } from "@/lib/useDrafts";
import { confirmDiscard } from "@/lib/unsavedChanges";
import { splitDocReviewFiles } from "@/lib/docReviewFiles";   // Batch 33 · T7
import { fmtNumber, fmtCount } from "@/components/Amount";   // STEP 3 · rule 2 · B-2e: the one amount formatter
import { useAuth } from "@/contexts/AuthContext";   // Step 11c · C3c · find 17
import { useReviewRoles, roleInSet } from "@/lib/reviewRoles";   // Step 11c · C3c · find 17: the server's own role set
import { methodText } from "@/pages/Sampling/evaluationFormat";   // Step 7a · S16: an old specific-vouching name shows the new reference

// ISA 315 (Revised) assertions
const TX_ASSERTIONS = ["Occurrence", "Completeness (Tx)", "Accuracy", "Cut-off",
                        "Classification (Tx)", "Presentation (Tx)"];
const BAL_ASSERTIONS = ["Existence", "Rights and Obligations", "Completeness (Bal)",
                         "Accuracy, Valuation and Allocation", "Classification (Bal)",
                         "Presentation (Bal)"];

// ---------- AssertionCell (unchanged from prior version) ----------
// Batch 28 · Follow-up 1 · F2 (b): with ``onSet(assertion, cell | null)`` the tick / clear / bypass goes into the sheet's row
// draft (saved with the table's Save); without it the cell writes at once, as before.
function AssertionCell({ item, assertion, onChange, onSet }) {
  const { t } = useI18n();
  const cell = (item.assertions || {})[assertion];
  const status = cell?.status;
  const [open, setOpen] = useState(false);
  const [remark, setRemark] = useState("");
  const [templates, setTemplates] = useState([]);

  useEffect(() => {
    if (open && templates.length === 0) {
      api.get("/bypass-templates").then((r) => setTemplates(r.data)).catch(() => {});
    }
  }, [open, templates.length]);

  const tick = async () => {
    if (onSet) { onSet(assertion, { status: "ticked", remark: "" }); return; }
    await api.patch(`/vouching/${item.id}/assertion`, { assertion, status: "ticked", remark: "" });
    onChange();
  };
  const clear = async () => {
    if (onSet) { onSet(assertion, null); return; }
    await api.patch(`/vouching/${item.id}/assertion`, { assertion, status: "cleared", remark: "" });
    onChange();
  };
  const bypass = async () => {
    const r = (remark || "").trim();
    if (!r) { toast.error(t("vouching.remarkRequired")); return; }
    if (onSet) { onSet(assertion, { status: "bypassed", remark: r }); setOpen(false); setRemark(""); return; }
    try {
      await api.patch(`/vouching/${item.id}/assertion`, { assertion, status: "bypassed", remark: r });
      setOpen(false); setRemark("");
      onChange();
    } catch (e) { toast.error(toErrMsg(e, "Failed")); }
  };
  const openBypass = () => { setRemark(cell?.remark || ""); setOpen(true); };

  return (
    <>
      <div className="flex items-center justify-center gap-0.5" data-testid={`assert-${item.id}-${assertion}`}>
        {status === "ticked" && (
          <button type="button" onClick={clear}
                   className="flex h-6 w-6 items-center justify-center rounded-md bg-emerald-100 text-emerald-700 hover:bg-emerald-200"
                   title={t("vouching.tickedTitle")}>
            <Check className="h-3.5 w-3.5" />
          </button>
        )}
        {status === "bypassed" && (
          <button type="button" onClick={openBypass}
                   className="flex h-6 w-6 items-center justify-center rounded-md bg-amber-100 text-amber-800 hover:bg-amber-200 text-[10px] font-bold"
                   title={`Bypassed — ${cell?.remark || ""}`}>
            B
          </button>
        )}
        {!status && (
          <>
            <button type="button" onClick={tick}
                     className="h-6 w-6 rounded-md border border-slate-300 hover:bg-emerald-50 hover:border-emerald-400"
                     title={t("vouching.tickTitle")} />
            <button type="button" onClick={openBypass}
                     className="text-[9px] px-1 rounded text-amber-700 hover:bg-amber-50"
                     title={t("vouching.bypassTitle")}>
              B
            </button>
          </>
        )}
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-600" />
              Bypass — {assertion}
            </DialogTitle>
            <DialogDescription>
              Voucher <b>{item.voucher_no}</b> · sample <b>{item.sample_item_uid || "—"}</b>. A
              remark is <b>mandatory</b> — the bypass and its reason stay visible on the vouching
              sheet and are written to the audit trail.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {templates.length > 0 && (
              <div>
                <label className="text-xs uppercase tracking-wider text-muted-foreground">{t("vouching.pickTemplate")}</label>
                <Select onValueChange={(v) => {
                  const tpl = templates.find((x) => x.id === v);
                  if (tpl) setRemark(tpl.text);
                }}>
                  <SelectTrigger data-testid={`bypass-tpl-${item.id}-${assertion}`}>
                    <SelectValue placeholder={t("vouching.pickTemplatePh")} />
                  </SelectTrigger>
                  <SelectContent>
                    {templates.map((tpl) => (
                      <SelectItem key={tpl.id} value={tpl.id}>{tpl.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="mt-1 text-[10px] text-muted-foreground italic">
                  {t("vouching.bypassTemplateHint")}
                </div>
              </div>
            )}
            <div>
            <label className="text-xs uppercase tracking-wider text-muted-foreground">{t("vouching.reasonRequired")}</label>
            <Textarea rows={4} value={remark} onChange={(e) => setRemark(e.target.value)}
                       placeholder={t("vouching.reasonPh")}
                       data-testid={`bypass-remark-${item.id}-${assertion}`} />
            </div>
          </div>
          <DialogFooter>
            {status === "bypassed" && (
              <Button variant="outline" onClick={async () => { await clear(); setOpen(false); }}>
                {t("vouching.clearBypassBtn")}
              </Button>
            )}
            <Button variant="outline" onClick={() => setOpen(false)}>{t("vouching.cancelBtn")}</Button>
            <Button onClick={bypass} disabled={!remark.trim()}
                     className="bg-amber-600 hover:bg-amber-700 text-white"
                     data-testid={`bypass-save-${item.id}-${assertion}`}>
              {t("vouching.saveBypassBtn")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}


// ==============================================================
// Vouching — top-level: landing list OR full-screen sheet editor.
// Combined multi-sheet view is intentionally removed; every sheet
// is standalone, opened from the list one at a time.
// ==============================================================
export default function Vouching() {
  const { t } = useI18n();
  const { current } = useEngagement();
  const [activeSheetId, setActiveSheetId] = useState(null);
  const [highlightItemId, setHighlightItemId] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();

  // Whenever the engagement changes, close any open sheet and return to list.
  useEffect(() => { setActiveSheetId(null); }, [current?.id]);

  // Deep-link support (Feb 2026):
  //   /vouching?sample=<sample_id>&highlight=<item_id>
  // Used by the Review Points dashboard "Open source" button so a
  // reviewer can jump straight from an RP row to the exact vouching
  // sheet / entry it was raised against. Cleared once consumed so a
  // hard refresh doesn't keep bouncing back.
  useEffect(() => {
    const sid = searchParams.get("sample");
    const hi = searchParams.get("highlight") || "";
    if (sid && current) {
      setActiveSheetId(sid);
      setHighlightItemId(hi);
      const next = new URLSearchParams(searchParams);
      next.delete("sample"); next.delete("highlight");
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line
  }, [searchParams.toString(), current?.id]);

  if (!current) return <div className="text-sm text-muted-foreground">{t("common.noEng")}</div>;

  if (activeSheetId) {
    return (
      <SheetEditor
        engagement={current}
        sampleId={activeSheetId}
        highlightItemId={highlightItemId}
        onBack={() => { setActiveSheetId(null); setHighlightItemId(""); }}
        t={t}
      />
    );
  }
  return (
    <VouchingLanding
      engagement={current}
      onOpen={(sid) => setActiveSheetId(sid)}
      t={t}
    />
  );
}


// ==============================================================
// Landing list — one row per vouching sheet on this engagement.
// ==============================================================
// Step 11c · C3c · find 17: POST /vouching/{eng}/backfill-groups admits the review roles only (backend review_roles.REVIEW_ROLES:
// Admin, Partner and the reviewer / manager tier) — a Preparer opening the list got a 403 on every open; the list itself never
// needed it. The screen asks the server's set (Rule 8: no role list of its own).

function VouchingLanding({ engagement, onOpen, t }) {
  const colW = useResizableColumns({ storageKey: "vouching-sheets", defaults: [240, 110, 110, 130, 240, 130, 130, 130], mins: [130, 72, 72, 90, 130, 90, 90, 90] });   // Step 11d · add-on
  const { lang, dict } = useI18n();
  const { user } = useAuth() || {};
  const rr = useReviewRoles();
  const mayBackfill = roleInSet(rr.data, "REVIEW_ROLES", user?.role);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortKey, setSortKey] = useState("updated_at");
  const [sortDir, setSortDir] = useState("desc");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Idempotent groups backfill — same as the old page did on mount, so
      // legacy rows still classify correctly when opened.
      if (mayBackfill) await api.post(`/vouching/${engagement.id}/backfill-groups`, null, { _silent: true }).catch(() => {});
      const r = await api.get(`/vouching/${engagement.id}/sheet-summary`);
      setRows(r.data || []);
    } finally { setLoading(false); }
  }, [engagement.id, mayBackfill]);
  useEffect(() => { load(); }, [load]);

  const sorted = useMemo(() => {
    const cmp = (a, b) => {
      let av = a[sortKey], bv = b[sortKey];
      if (typeof av === "string") { av = av.toLowerCase(); bv = (bv || "").toLowerCase(); }
      if (av === bv) return 0;
      return (av > bv ? 1 : -1) * (sortDir === "asc" ? 1 : -1);
    };
    return [...rows].sort(cmp);
  }, [rows, sortKey, sortDir]);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir(key === "name" ? "asc" : "desc"); }
  };
  const SortBtn = ({ k, children }) => (
    <button type="button" onClick={() => toggleSort(k)}
            className="flex items-center gap-1 hover:text-slate-900 dark:hover:text-slate-100"
            data-testid={`vouch-sort-${k}`}>
      {children}
      <ArrowUpDown className={`h-3 w-3 ${sortKey === k ? "text-emerald-600" : "text-slate-300"}`} />
    </button>
  );

  return (
    <div className="space-y-6" data-testid="vouching-landing">
      <LockedBanner engagement={engagement} />
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">{t("vouching.title")}</h1>
        <p className="text-sm text-muted-foreground">
          {fmtPlural(rows.length, lang, dict.plurals.sheets)} on this engagement · click any sheet to open it
        </p>
      </div>

      {/* One-click swap: replace V-#### placeholders with real vouchers
          pulled from the current GL. Runs synchronously — the underlying
          query is O(placeholders) with a batched id lookup. */}
      <Card className="border-emerald-200 bg-emerald-50/40 dark:border-emerald-900 dark:bg-emerald-950/20">
        <CardContent className="flex flex-wrap items-center justify-between gap-3 p-4">
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold">{t("vouching.backfillTitle")}</div>
            <div className="text-[11px] text-muted-foreground">
              {t("vouching.backfillBodyPre")}<b>{t("vouching.backfillVoucherUin")}</b>{t("vouching.backfillBodyMid")}<span className="rounded bg-white px-1 font-mono dark:bg-slate-900">V-####</span>{t("vouching.backfillBodyPost")}
            </div>
          </div>
          <Button size="sm" variant="outline"
                  className="border-emerald-300 bg-white hover:bg-emerald-50 dark:bg-slate-900"
                  data-testid="vouch-backfill-btn"
                  onClick={async () => {
                    if (!await askConfirm(t("vouching.backfillConfirm"))) return;
                    try {
                      const r = await api.post(`/vouching/${engagement.id}/backfill-from-gl`);
                      const d = r.data || {};
                      if (d.updated > 0) {
                        toast.success(t("vouching.backfilled").replace("{n}", fmtCount(d.updated)).replace("{left}", fmtCount(d.remaining_placeholders)));
                      } else {
                        toast.info(d.message || t("vouching.backfillNothing").replace("{n}", d.still_blank_gl?.toLocaleString?.() || 0));
                      }
                      await load();
                    } catch (e) {
                      toast.error(toErrMsg(e, t("vouching.backfillFailed")));
                    }
                  }}>
            <Wand2 className="me-1 h-3 w-3" /> {t("vouching.backfillBtn")}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <ResizableTable cols={colW} wrap className="w-full text-sm">
            <thead className="border-b bg-slate-50 text-xs uppercase text-muted-foreground dark:bg-slate-900/40">
              <tr>
                <ResizableTh index={0} className="px-4 py-2 text-start"><SortBtn k="name">{t("vouching.colName")}</SortBtn></ResizableTh>
                <ResizableTh index={1} className="px-4 py-2 text-start">{t("samplingSelector.folderRef")}</ResizableTh>
                <ResizableTh index={2} className="px-4 py-2 text-start">{t("vouching.colMethod")}</ResizableTh>
                <ResizableTh index={3} className="px-4 py-2 text-end">{t("vouching.colItems")}</ResizableTh>
                <ResizableTh index={4} className="px-4 py-2 text-start w-48"><SortBtn k="pct_by_count">{t("vouching.colItemCompletion")}</SortBtn></ResizableTh>
                <ResizableTh index={5} className="px-4 py-2 text-end">{t("vouching.colTotalPop")}</ResizableTh>
                <ResizableTh index={6} className="px-4 py-2 text-end">{t("vouching.colAmountVerified")}</ResizableTh>
                <ResizableTh index={7} className="px-4 py-2 text-start w-40"><SortBtn k="pct_by_value">{t("vouching.colPctByValue")}</SortBtn></ResizableTh>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={8} className="px-4 py-8 text-center text-muted-foreground">{t("vouching.loadingSheets")}</td></tr>
              )}
              {!loading && sorted.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-muted-foreground">
                    {t("vouching.emptyList")}
                  </td>
                </tr>
              )}
              {!loading && sorted.map((r) => (
                <tr key={r.sample_id}
                    onClick={() => onOpen(r.sample_id)}
                    className="cursor-pointer border-b hover:bg-slate-50 dark:hover:bg-slate-900/50"
                    data-testid={`vouch-sheet-row-${r.sample_id}`}>
                  <td className="px-4 py-3 font-medium">
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4 text-slate-400" />
                      <button type="button"
                              className="text-slate-900 hover:text-emerald-700 hover:underline dark:text-slate-100"
                              data-testid={`vouch-open-${r.sample_id}`}>
                        {r.name}
                      </button>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {r.file_in_folder ? (() => {
                      const raw = String(r.file_in_folder);
                      // Batch 10 · B14: the slot the sheet is FILED under — -03 "Sample link" (a sample), -04 "Vouching sheet(s)"
                      // (Specific vouching, A19); before, every row said -03
                      const cat = r.folder_category || (r.not_a_sample ? "04" : "03");
                      const pill = raw.toLowerCase() === "a9001" ? `A9001-${cat}`
                                  : raw.toLowerCase() === "a9002" ? `A9002-${cat}`
                                  : `${raw}-${cat}`;
                      const href = raw.toLowerCase().startsWith("a900")
                                  ? `/head-folders#${raw.toLowerCase()}-${cat}`
                                  : `/head-folders#${raw}-${cat}`;
                      return (
                        <a href={href}
                           className="inline-flex items-center gap-1 rounded-full border border-sky-300 bg-sky-50 px-2 py-0.5 font-mono text-[10px] font-semibold text-sky-800 hover:bg-sky-100 dark:border-sky-800 dark:bg-sky-950/40 dark:text-sky-200"
                           onClick={(e) => e.stopPropagation()}
                           data-testid={`vouch-folder-pill-${r.sample_id}`}>
                          <Ref>{refLabel(pill, lang)}</Ref>
                        </a>
                      );
                    })() : (<span className="text-[10px] text-muted-foreground">—</span>)}
                  </td>
                  <td className="px-4 py-3">{methodText(r.method, t)}</td>
                  <td className="px-4 py-3 num-cell">{r.sample_size}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Progress value={r.pct_by_count} className="h-2 flex-1"
                                data-testid={`vouch-progress-count-${r.sample_id}`} />
                      <span className="w-16 text-end tabular-nums text-xs text-slate-700 dark:text-slate-200">
                        {r.pct_by_count}%
                      </span>
                    </div>
                    <div className="mt-0.5 text-[10px] text-muted-foreground">
                      {t("vouching.itemsTestedFmt").replace("{tested}", r.tested_count).replace("{total}", r.sample_size)}
                    </div>
                  </td>
                  <td className="px-4 py-3 num-cell">{fmtCount(r.population_size || 0)}</td>
                  <td className="px-4 py-3 num-cell text-emerald-700">
                    {fmtNumber(Number(r.amount_verified || 0))}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="relative h-2 flex-1 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-800"
                            data-testid={`vouch-progress-value-${r.sample_id}`}>
                        <div className="h-full rounded-full bg-amber-500 transition-all"
                              style={{ width: `${Math.min(100, r.pct_by_value)}%` }} />
                      </div>
                      <span className="w-16 text-end tabular-nums text-xs text-amber-700">
                        {r.pct_by_value}%
                      </span>
                    </div>
                    {r.value_basis === "selected_items" && (   /* Batch 14 · M1 (F-100) */
                      <div className="mt-0.5 text-[10px] text-sky-800" data-testid={`vouch-value-basis-${r.sample_id}`}>{t("vouching.ofSelectedItems")} · {t("vouching.notProjected")}</div>
                    )}
                    <div className="mt-0.5 text-[10px] text-muted-foreground italic">
                      {t("vouching.byMonetary")}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </ResizableTable>
        </CardContent>
      </Card>
    </div>
  );
}


// ==============================================================
// Full-screen sheet editor — mirrors the Planning form pattern:
// sticky header + back link + existing VouchingTable + filters.
// ==============================================================
export function SheetEditor({ engagement, sampleId, highlightItemId, onBack, t }) {
  const { lang, dict } = useI18n();
  const [sample, setSample] = useState(null);
  const [summary, setSummary] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [assertionFilter, setAssertionFilter] = useState("__any__");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ssRes, smRes, viRes] = await Promise.all([
        api.get(`/samples/${engagement.id}`),
        api.get(`/vouching/${engagement.id}/sheet-summary`),
        api.get(`/vouching/${engagement.id}?sample_id=${sampleId}`),
      ]);
      const s = asArray(ssRes.data).find((x) => x.id === sampleId);
      const su = asArray(smRes.data).find((x) => x.sample_id === sampleId);
      setSample(s); setSummary(su); setItems(viRes.data || []);
    } finally { setLoading(false); }
  }, [engagement.id, sampleId]);
  useEffect(() => { load(); }, [load]);
  // Batch 28 · Follow-up 1 · F2 (b): the sheet's rows are drafts — one Save / Cancel bar under the table; Save writes only the
  // changed rows, each on its own (PATCH /vouching/{id} for tested / bypass / remarks / support reference, PATCH
  // /vouching/{id}/assertion per changed assertion — one Audit Trail row each, as before); a failed row stays highlighted.
  async function saveVouchRow(id, patch, row) {
    const { assertions, ...rest } = patch;
    if (assertions) {
      const before = (row && row.assertions) || {};
      for (const a of new Set([...Object.keys(before), ...Object.keys(assertions)])) {
        const n = assertions[a] || null;
        const o = before[a] || null;
        if ((n && n.status) === (o && o.status) && ((n && n.remark) || "") === ((o && o.remark) || "")) continue;
        await api.patch(`/vouching/${id}/assertion`, n ? { assertion: a, status: n.status, remark: n.remark || "" }
                                                         : { assertion: a, status: "cleared", remark: "" });
      }
    }
    if (Object.keys(rest).length) await api.patch(`/vouching/${id}`, rest);
  }
  // Step 11c · C3c · find 19: Save sends every changed row in ONE request (POST …/save-rows; the same rules and audit rows as
  // the two per-row PATCH routes, which saveVouchRow above still uses for a single-row save path)
  async function saveVouchRows(list) {
    const rows = list.map(({ id, patch, row }) => {
      const { assertions, ...rest } = patch;
      const cells = {};
      if (assertions) {
        const before = (row && row.assertions) || {};
        for (const a of new Set([...Object.keys(before), ...Object.keys(assertions)])) {
          const n = assertions[a] || null;
          const o = before[a] || null;
          if ((n && n.status) === (o && o.status) && ((n && n.remark) || "") === ((o && o.remark) || "")) continue;
          cells[a] = n ? { status: n.status, remark: n.remark || "" } : null;
        }
      }
      return { id, update: rest, assertions: cells };
    });
    const r = await api.post(`/vouching/${engagement.id}/sheet/${sampleId}/save-rows`, { rows });
    const failed = {};
    Object.entries((r.data && r.data.failed) || {}).forEach(([id, f]) => {
      failed[id] = (lang === "ar" && f.detail_ar) || f.message || "Not saved";
    });
    return { saved: (r.data && r.data.saved) || [], failed };
  }
  const td = useTableDraft({ rows: items, saveRow: saveVouchRow, saveRows: saveVouchRows, reload: load });
  const back = async () => { if (await confirmDiscard(td.dirty, lang)) onBack(); };

  const toggleTested = async (item, checked) => {
    await api.patch(`/vouching/${item.id}`, { tested: checked });
    setItems((prev) => prev.map((i) => (i.id === item.id ? { ...i, tested: checked } : i)));
    // Refresh summary so amount_verified / pct-by-value are in sync
    api.get(`/vouching/${engagement.id}/sheet-summary`)
      .then((r) => setSummary(asArray(r.data).find((x) => x.sample_id === sampleId)))
      .catch(() => {});
  };
  const updateRemarks = (item, val) =>
    setItems((prev) => prev.map((i) => (i.id === item.id ? { ...i, remarks: val } : i)));
  // Batch 9 · A19: the support reference is editable; a supporting document can be attached on the row
  const saveSupportRef = async (item, value) => {
    try { await api.patch(`/vouching/${item.id}`, { support_doc_reference: value }); await load(); }
    catch (e) { toast.error(toErrMsg(e, "Save failed")); }
  };
  const uploadRowDoc = async (item, file) => {
    if (!file) return;
    if (splitDocReviewFiles([file]).rejected.length) {   // Batch 33 · T7: the upload takes PDF / images only — say so, send nothing
      toast.warning(t("b33ui.filesNotAdded").replace("{n}", "1"));
      return;
    }
    try {
      // Batch 35 · B35-10: doc_type "auto" (the classifier decides, as on Documents); the PATCH links the document only — the
      // server's refresh sets the automatic reference (sending a reference here marked it typed and froze it)
      const fd = new FormData(); fd.append("file", file); fd.append("engagement_id", engagement.id); fd.append("doc_type", "auto"); fd.append("force", "1");
      const r = await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.patch(`/vouching/${item.id}`, { document_ids: [...(item.document_ids || []), r.data.id] });
      toast.success(t("mfa.uploadOk") || "File added"); await load();
    } catch (e) { toast.error(toErrMsg(e, "Upload failed")); }
  };
  const saveRemarks = async (item) => {
    await api.patch(`/vouching/${item.id}`, { remarks: item.remarks });
    toast.success(t("vouching.savedToast"));
  };
  const toggleBypass = async (item, checked) => {
    await api.patch(`/vouching/${item.id}`, { bypass_missing_doc: checked });
    setItems((prev) => prev.map((i) => (i.id === item.id ? { ...i, bypass_missing_doc: checked } : i)));
    toast.success(checked ? t("vouching.missingDocBypassed") : t("vouching.bypassCleared"));
  };

  const copyFromPrev = async (item) => {
    const currentIdx = items.findIndex((i) => i.id === item.id);
    const prev = items.slice(0, currentIdx).reverse()
      .find((i) => i.assertions && Object.keys(i.assertions).length > 0);
    if (!prev) { toast.error(t("vouching.noPrevRow")); return; }
    try {
      const r = await api.post(`/vouching/${item.id}/copy-assertions-from/${prev.id}`);
      toast.success(t("vouching.copiedAssertions").replace("{n}", r.data.copied).replace("{voucher}", prev.voucher_no));
      await load();
    } catch (e) { toast.error(toErrMsg(e, t("vouching.copyFailed"))); }
  };

  const exportXlsx = async () => {
    const token = localStorage.getItem("af_token");
    const url = `${API_URL}/vouching/${engagement.id}/export?sample_id=${sampleId}`;
    const resp = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await resp.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `vouching_${sample?.name || sampleId}.xlsx`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const testedCount = items.filter((i) => i.tested).length;
  const progressPct = items.length ? (testedCount / items.length) * 100 : 0;
  const ALL_ASSERTIONS = [...TX_ASSERTIONS, ...BAL_ASSERTIONS];
  const filteredItems = items.filter((it) => {
    const a = it.assertions || {};
    const gotAny = Object.values(a).some((c) => c?.status === "ticked" || c?.status === "bypassed");
    const anyBypass = Object.values(a).some((c) => c?.status === "bypassed");
    if (filter === "any-bypass" && !anyBypass) return false;
    if (filter === "no-assertions" && gotAny) return false;
    if (filter === "fully-ticked") {
      const ticked = ALL_ASSERTIONS.every((k) => a[k]?.status === "ticked");
      if (!ticked) return false;
    }
    if (filter === "missing-assertion" && assertionFilter !== "__any__") {
      const s = a[assertionFilter]?.status;
      if (s === "ticked" || s === "bypassed") return false;
    }
    return true;
  });

  return (
    <ReviewCountsProvider engagementId={engagement.id} targetType="vouching_entry">
    <div className="space-y-4" data-testid="vouching-sheet-editor">
      <LockedBanner engagement={engagement} />
      {/* Batch 11 · A2 — the sample's sampling sheet and its vouching sheet each carry a sign-off bar */}
      <SignOffBar engId={engagement.id} itemType="sampling" itemKey={sampleId} label={t("itemSignoff.samplingSheet")} />
      <SignOffBar engId={engagement.id} itemType="vouching" itemKey={sampleId} label={t("itemSignoff.vouchingSheet")} />

      {/* Sticky header — pinned to the top of the scrolling <main>. Uses
          top-0 because <main> is the scroll container (fixed-chrome
          layout), so top-0 sits flush against the app top bar. */}
      <div className="sticky top-0 z-10 -mx-6 border-b bg-white/95 px-6 py-3 backdrop-blur dark:bg-slate-950/95 dark:border-slate-800">
        <button type="button" onClick={back}
                className="flex items-center gap-1 text-xs text-emerald-700 hover:underline dark:text-emerald-400"
                data-testid="vouch-back-to-list">
          <ChevronLeft className="h-3 w-3" /> {t("vouching.backToList")}
        </button>
        <div className="mt-2 flex flex-wrap items-end justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-semibold tracking-tight">{sample?.name || t("vouching.sheetTitleFallback")}</h1>
              {/* Sheet-level Review Points — badge + Add Review pill */}
              <ReviewControls engagementId={engagement.id}
                              targetType="vouching_sheet"
                              targetId={sampleId}
                              targetLabel={sample?.name || t("vouching.sheetTitleFallback")} />
            </div>
            <p className="text-xs text-muted-foreground">
              {methodText(sample?.method, t)} · {fmtPlural(items.length, lang, dict.plurals.items)} {t("vouching.onThisSheet")}
              {sample?.not_a_sample && <span className="ms-2 rounded border border-sky-300 bg-sky-50 px-1 text-[10px] text-sky-900 dark:bg-sky-950/30" data-testid="sheet-not-a-sample">{lang === "ar" ? "ليست عينة — بلا إسقاط" : "Not a sample — no projection"}</span>}
            </p>
            {sample?.not_a_sample && (
              <p className="text-[11px] text-muted-foreground" data-testid="sheet-specific-line">
                {lang === "ar" ? "اختيار بنود محددة — ليس عينة؛ لا تُسقَط النتائج على المجتمع (معيار المراجعة الدولي 500)" : "Selecting specific items — not sampling; results are not projected (ISA 500)"}
              </p>
            )}
          </div>
          <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs md:grid-cols-4">
            <MetricPill label={t("vouching.colItemCompletion")} value={`${testedCount}/${items.length}`}
                        sub={`${Math.round(progressPct)}%`} tone="emerald"
                        testid="sheet-metric-count" />
            <MetricPill label={t("vouching.colTotalPop")} value={fmtCount(summary?.population_size || 0)}
                        sub={summary?.population_value ? t("vouching.metricPopValueTail").replace("{n}", fmtNumber(Number(summary.population_value), { decimals: 0 })) : "—"}
                        tone="slate" testid="sheet-metric-pop" />
            <MetricPill label={t("vouching.colAmountVerified")} value={fmtNumber(Number(summary?.amount_verified || 0), { decimals: 0 })}
                        sub={t("vouching.metricTestedOnly")} tone="emerald" testid="sheet-metric-verified" />
            <MetricPill label={t("vouching.colPctByValue")} value={`${summary?.pct_by_value || 0}%`}
                        sub={summary?.value_basis === "selected_items" ? `${t("vouching.ofSelectedItems")} · ${t("vouching.notProjected")}` : t("vouching.byMonetary")} tone="amber" testid="sheet-metric-pct-value" />
          </div>
        </div>
      </div>

      {/* Sheet-level progress bar + filters (mirrors the previous inline view) */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">{t("vouching.progress")}</CardTitle>
            <div className="flex items-center gap-2">
              <div className="text-sm tabular-nums">{testedCount} / {items.length}</div>
              <Button variant="outline" onClick={exportXlsx} data-testid="vouch-export" size="sm">
                <Download className="me-1 h-4 w-4" /> {t("vouching.exportXlsx")}
              </Button>
            </div>
          </div>
          <Progress value={progressPct} className="h-2" data-testid="vouch-progress" />
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Filter className="h-3 w-3 text-muted-foreground" />
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="h-7 w-56 text-xs" data-testid="vouch-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("vouching.filterAllRows").replace("{n}", items.length)}</SelectItem>
                <SelectItem value="missing-assertion">{t("vouching.filterMissingAssertion")}</SelectItem>
                <SelectItem value="any-bypass">{t("vouching.filterAnyBypass")}</SelectItem>
                <SelectItem value="no-assertions">{t("vouching.filterNoAssertions")}</SelectItem>
                <SelectItem value="fully-ticked">{t("vouching.filterFullyTicked")}</SelectItem>
              </SelectContent>
            </Select>
            {filter === "missing-assertion" && (
              <Select value={assertionFilter} onValueChange={setAssertionFilter}>
                <SelectTrigger className="h-7 w-56 text-xs" data-testid="vouch-assertion-filter">
                  <SelectValue placeholder={t("vouching.whichAssertion")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__any__">{t("vouching.pickAssertion")}</SelectItem>
                  {TX_ASSERTIONS.map((a) => <SelectItem key={a} value={a}>{assertionLabel(a, t)} {t("r56.assertions.tx")}</SelectItem>)}
                  {BAL_ASSERTIONS.map((a) => <SelectItem key={a} value={a}>{assertionLabel(a, t)} {t("r56.assertions.bal")}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            <span className="text-[10px] text-muted-foreground">
              {t("vouching.showingCount").replace("{shown}", filteredItems.length).replace("{total}", items.length)}
            </span>
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-3 text-[10px] text-muted-foreground">
            <span className="flex items-center gap-1"><span className="inline-block h-3 w-3 rounded border border-slate-300" />{t("vouching.legendNotTested")}</span>
            <span className="flex items-center gap-1"><span className="inline-flex h-3 w-3 items-center justify-center rounded bg-emerald-100 text-emerald-700"><Check className="h-2 w-2" /></span>{t("vouching.legendTested")}</span>
            <span className="flex items-center gap-1"><span className="inline-flex h-3 w-3 items-center justify-center rounded bg-amber-100 text-amber-800 text-[8px] font-bold">B</span>{t("vouching.legendBypassed")}</span>
            <span className="italic">{t("vouching.legendIsa315")}</span>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-sm text-muted-foreground">{t("vouching.loadingSheet")}</div>
          ) : (
            <VouchingTable
              items={filteredItems}
              t={t}
              AssertionCell={AssertionCell}
              copyFromPrev={copyFromPrev}
              toggleTested={toggleTested}
              toggleBypass={toggleBypass}
              updateRemarks={updateRemarks}
              saveRemarks={saveRemarks}
              saveSupportRef={saveSupportRef}
              uploadRowDoc={uploadRowDoc}
              load={load}
              engagementId={engagement.id}
              sampleId={sampleId}
              highlightItemId={highlightItemId}
              draft={td}
            />
          )}
          {!loading && <TableSaveBar draft={td} lang={lang} testid="vouch-table-save" />}
        </CardContent>
      </Card>

      {/* Work Done — always visible below the assertion table.
          Additive only: does NOT touch tick marks, bypass, or progress. */}
      {!loading && items.length > 0 && (
        <WorkDoneSection
          engagementId={engagement.id}
          sampleId={sampleId}
          locked={!!engagement.signed_off_at}
        />
      )}
    </div>
    </ReviewCountsProvider>
  );
}


function MetricPill({ label, value, sub, tone, testid }) {
  const toneClass = {
    emerald: "text-emerald-700",
    amber: "text-amber-700",
    slate: "text-slate-700 dark:text-slate-200",
  }[tone] || "text-slate-700";
  return (
    <div className="min-w-[8rem]" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={`text-base font-semibold tabular-nums ${toneClass}`}>{value}</div>
      {sub && <div className="text-[10px] text-muted-foreground italic">{sub}</div>}
    </div>
  );
}


// ==============================================================
// Work Done section — always visible below the assertion table.
// Additive only: it does NOT read from or write to the assertion
// tick-mark table, its bypass logic, or any progress calculation.
// ==============================================================
function WorkDoneSection({ engagementId, sampleId, locked }) {
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/vouching/${engagementId}/sheet/${sampleId}/work-done`);
      setData(r.data);
    } finally { setLoading(false); }
  }, [engagementId, sampleId]);
  useEffect(() => { load(); }, [load]);

  const saveAssertion = async (assertion, text) => {
    setSaving(true);
    try {
      await api.patch(
        `/vouching/${engagementId}/sheet/${sampleId}/work-done/assertion`,
        { assertion, text });
      await load();
    } catch (e) {
      toast.error(toErrMsg(e, "Save failed"));
    } finally { setSaving(false); }
  };
  const addLine = async () => {
    setSaving(true);
    try {
      await api.post(`/vouching/${engagementId}/sheet/${sampleId}/work-done/line`,
                        { text: "" });
      await load();
    } catch (e) {
      toast.error(toErrMsg(e, "Failed to add line"));
    } finally { setSaving(false); }
  };
  const saveLine = async (lineId, text) => {
    setSaving(true);
    try {
      await api.patch(
        `/vouching/${engagementId}/sheet/${sampleId}/work-done/line/${lineId}`,
        { text });
      await load();
    } catch (e) {
      toast.error(toErrMsg(e, "Save failed"));
    } finally { setSaving(false); }
  };
  const deleteLine = async (lineId) => {
    setSaving(true);
    try {
      await api.delete(
        `/vouching/${engagementId}/sheet/${sampleId}/work-done/line/${lineId}`);
      await load();
    } catch (e) {
      toast.error(toErrMsg(e, "Delete failed"));
    } finally { setSaving(false); }
  };

  if (loading || !data) {
    return (
      <Card data-testid="work-done-section">
        <CardHeader><CardTitle className="text-base">{t("vouching.workDone")}</CardTitle></CardHeader>
        <CardContent><div className="text-sm text-muted-foreground">{t("vouching.loading")}</div></CardContent>
      </Card>
    );
  }

  const kindLabel = data.sheet_kind === "balance"
    ? t("vouching.balanceLevel") : t("vouching.transactionLevel");

  return (
    <Card data-testid="work-done-section">
      <CardHeader className="pb-2">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <CardTitle className="text-base">{t("vouching.workDone")}</CardTitle>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
            {kindLabel} · {t("vouching.workDoneClickHint")}
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <table className="w-full text-sm">
          <thead className="border-b bg-slate-50 text-xs uppercase text-muted-foreground dark:bg-slate-900/40">
            <tr>
              <th className="w-56 px-4 py-2 text-start">{t("vouching.colAssertion")}</th>
              <th className="px-4 py-2 text-start">{t("vouching.colProcedure")}</th>
              <th className="w-40 px-4 py-2 text-start">{t("vouching.colLastEdited")}</th>
            </tr>
          </thead>
          <tbody>
            {data.assertions.map((row) => (
              <WorkDoneRow
                key={row.assertion}
                label={row.assertion}
                text={row.text}
                isDefault={row.is_default}
                updatedByName={row.updated_by_name}
                updatedAt={row.updated_at}
                locked={locked || saving}
                onSave={(text) => saveAssertion(row.assertion, text)}
                testid={`work-done-row-${row.assertion}`}
              />
            ))}
            {(data.additional_lines || []).length > 0 && (
              <tr className="bg-slate-50/60 dark:bg-slate-900/30">
                <td colSpan={3} className="px-4 py-1 text-[10px] uppercase tracking-wider text-muted-foreground">
                  {t("vouching.additionalProcedures")}
                </td>
              </tr>
            )}
            {asArray(data.additional_lines).map((line) => (
              <WorkDoneRow
                key={line.id}
                label="—"
                text={line.text || ""}
                placeholder={t("vouching.procedurePh")}
                updatedByName={line.updated_by_name}
                updatedAt={line.updated_at}
                locked={locked || saving}
                onSave={(text) => saveLine(line.id, text)}
                onDelete={() => deleteLine(line.id)}
                testid={`work-done-line-${line.id}`}
              />
            ))}
          </tbody>
        </table>
        <div className="border-t p-3">
          <Button variant="outline" size="sm" onClick={addLine}
                    disabled={locked || saving} data-testid="work-done-add-line">
            <Plus className="me-1 h-3 w-3" /> {t("vouching.addLineBtn")}
          </Button>
          <span className="ms-3 text-[10px] text-muted-foreground">
            {t("vouching.defaultWording")}
          </span>
        </div>
      </CardContent>
    </Card>
  );
}


function WorkDoneRow({ label, text, isDefault, placeholder, updatedByName,
                        updatedAt, locked, onSave, onDelete, testid }) {
  const { t } = useI18n();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(text);
  const [hover, setHover] = useState(false);
  useEffect(() => { setDraft(text); }, [text]);
  const beginEdit = () => { if (!locked) { setDraft(text); setEditing(true); } };
  const commit = async () => {
    const nextText = (draft || "").trim();
    setEditing(false);
    if (nextText === (text || "").trim()) return;
    await onSave(nextText);
  };
  const cancel = () => { setDraft(text); setEditing(false); };

  return (
    <tr className="border-b align-top hover:bg-slate-50/60 dark:hover:bg-slate-900/40"
        onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
        data-testid={testid}>
      <td className="px-4 py-3 font-medium text-xs text-slate-700 dark:text-slate-200">
        {label}
      </td>
      <td className="px-4 py-3">
        {editing ? (
          <Textarea autoFocus rows={2}
                     value={draft}
                     onChange={(e) => setDraft(e.target.value)}
                     onBlur={commit}
                     onKeyDown={(e) => {
                       if (e.key === "Enter" && !e.shiftKey) {
                         e.preventDefault(); commit();
                       } else if (e.key === "Escape") {
                         e.preventDefault(); cancel();
                       } else if (e.key === "Tab") {
                         commit();
                       }
                     }}
                     className="text-sm"
                     data-testid={`${testid}-input`} />
        ) : (
          <div
            role="button" tabIndex={0}
            onClick={beginEdit}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") beginEdit(); }}
            className={`group flex cursor-text items-start gap-2 rounded px-1 py-0.5 -mx-1 -my-0.5 ${
              locked ? "cursor-not-allowed opacity-70" : "hover:bg-slate-100 dark:hover:bg-slate-800/40"
            }`}
            data-testid={`${testid}-text`}>
            <span className={`flex-1 text-sm ${
              isDefault ? "text-slate-500 dark:text-slate-400 italic" : "text-slate-800 dark:text-slate-100"
            }`}>
              {text || placeholder || <span className="text-slate-400">{t("vouching.clickToAdd")}</span>}
            </span>
            <Pencil className={`mt-0.5 h-3 w-3 shrink-0 text-slate-400 transition-opacity ${
              hover && !locked ? "opacity-100" : "opacity-0"
            }`} />
          </div>
        )}
      </td>
      <td className="px-4 py-3 text-[11px] text-muted-foreground">
        <div className="flex items-start gap-2">
          <div className="flex-1">
            {updatedByName ? (
              <>
                <div className="font-medium text-slate-600 dark:text-slate-300">{updatedByName}</div>
                <div className="text-[10px]">{fmtDateDt(updatedAt)}</div>
              </>
            ) : (
              <span className="text-slate-400 italic">{t("vouching.defaultText")}</span>
            )}
          </div>
          {onDelete && !locked && (
            <button type="button" onClick={onDelete}
                     title={t("vouching.removeLine")}
                     className="rounded p-1 text-slate-400 hover:bg-red-50 hover:text-red-600"
                     data-testid={`${testid}-delete`}>
              <Trash2 className="h-3 w-3" />
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}
